// Field Restrictions and Validation System
export const FIELD_RESTRICTIONS = {
    // Parameter Type → Allowed UI Types
    parameter_type: {
        BOOLEAN: { 
            allowed_types: ['switch', 'select', 'radio', 'checkbox'],
            description: 'Boolean values need toggle or choice UI'
        },
        STRING: { 
            allowed_types: ['text', 'textarea', 'select', 'radio', 'checkbox'],
            description: 'String values need text input or choice UI'
        },
        INTEGER: { 
            allowed_types: ['number', 'text', 'select', 'radio'],
            description: 'Integer values need numeric input or choice UI'
        },
        FLOAT: { 
            allowed_types: ['number', 'text', 'select', 'radio'],
            description: 'Float values need numeric input or choice UI'
        },
        JSON: { 
            allowed_types: ['textarea', 'text'],
            description: 'JSON values need text input for editing'
        }
    },

    // UI Type → Requirements
    type: {
        select: { 
            requires_options: true,
            requires_option_type: true,
            description: 'Select fields need options to choose from'
        },
        radio: { 
            requires_options: true,
            requires_option_type: true,
            description: 'Radio fields need options to choose from'
        },
        checkbox: { 
            requires_options: true,
            requires_option_type: true,
            description: 'Checkbox fields need options to choose from'
        },
        switch: { 
            requires_options: false,
            description: 'Switch fields work with boolean values only'
        },
        text: { 
            requires_options: false,
            description: 'Text fields work with any text value'
        },
        textarea: { 
            requires_options: false,
            description: 'Textarea fields work with any text value'
        },
        number: { 
            requires_options: false,
            description: 'Number fields work with numeric values only'
        }
    },

    // Option Type → Requirements
    option_type: {
        STATIC: { 
            requires_options: true,
            requires_config: false,
            description: 'Static options need predefined options array'
        },
        API: { 
            requires_options: false,
            requires_config: true,
            description: 'API options need endpoint configuration'
        }
    },

    // Validation Rules → Parameter Type
    validation_rules: {
        BOOLEAN: {
            allowed: [],
            restricted: ['minLength', 'maxLength', 'pattern', 'email', 'url', 'min', 'max', 'precision'],
            description: 'Boolean values don\'t need validation rules'
        },
        STRING: {
            allowed: ['minLength', 'maxLength', 'pattern', 'email', 'url'],
            restricted: ['min', 'max', 'precision'],
            description: 'String values can have text-based validation'
        },
        INTEGER: {
            allowed: ['min', 'max'],
            restricted: ['minLength', 'maxLength', 'pattern', 'email', 'url', 'precision'],
            description: 'Integer values can have numeric validation'
        },
        FLOAT: {
            allowed: ['min', 'max', 'precision'],
            restricted: ['minLength', 'maxLength', 'pattern', 'email', 'url'],
            description: 'Float values can have numeric validation with precision'
        },
        JSON: {
            allowed: [],
            restricted: ['minLength', 'maxLength', 'pattern', 'email', 'url', 'min', 'max', 'precision'],
            description: 'JSON values are validated by JSON.parse()'
        }
    }
};

// Validation Functions
export const validateParameterTypeCompatibility = (parameterType, uiType) => {
    const restrictions = FIELD_RESTRICTIONS.parameter_type[parameterType];
    if (!restrictions) return { valid: false, message: 'Unknown parameter type' };
    
    const isCompatible = restrictions.allowed_types.includes(uiType);
    return {
        valid: isCompatible,
        message: isCompatible ? null : restrictions.description,
        suggestions: restrictions.allowed_types
    };
};

export const validateOptionsRequirements = (uiType, options, optionType, optionConfig) => {
    const restrictions = FIELD_RESTRICTIONS.type[uiType];
    if (!restrictions) return { valid: true, message: null };

    if (restrictions.requires_options) {
        if (!options || (Array.isArray(options) && options.length === 0)) {
            return {
                valid: false,
                message: `${uiType} fields require options to be defined`,
                suggestions: ['Add options in the Options tab']
            };
        }
    }

    if (restrictions.requires_option_type) {
        if (!optionType) {
            return {
                valid: false,
                message: `${uiType} fields require option type to be set`,
                suggestions: ['Set option type to STATIC or API']
            };
        }
    }

    return { valid: true, message: null };
};

export const validateOptionTypeRequirements = (optionType, options, optionConfig) => {
    const restrictions = FIELD_RESTRICTIONS.option_type[optionType];
    if (!restrictions) return { valid: true, message: null };

    if (restrictions.requires_options) {
        if (!options || (Array.isArray(options) && options.length === 0)) {
            return {
                valid: false,
                message: `STATIC option type requires options to be defined`,
                suggestions: ['Add options in the Options tab']
            };
        }
    }

    if (restrictions.requires_config) {
        if (!optionConfig || !optionConfig.endpoint) {
            return {
                valid: false,
                message: `API option type requires endpoint configuration`,
                suggestions: ['Select an internal API endpoint from the dropdown']
            };
        }
    }

    return { valid: true, message: null };
};

export const validateValidationRules = (parameterType, validationRules) => {
    if (!validationRules) return { valid: true, message: null };

    const restrictions = FIELD_RESTRICTIONS.validation_rules[parameterType];
    if (!restrictions) return { valid: true, message: null };

    try {
        const rules = typeof validationRules === 'string' 
            ? JSON.parse(validationRules) 
            : validationRules;

        const invalidRules = Object.keys(rules).filter(rule => 
            restrictions.restricted.includes(rule)
        );

        if (invalidRules.length > 0) {
            return {
                valid: false,
                message: `${parameterType} type doesn't support these validation rules: ${invalidRules.join(', ')}`,
                suggestions: restrictions.allowed
            };
        }

        return { valid: true, message: null };
    } catch (e) {
        return {
            valid: false,
            message: 'Invalid JSON format in validation rules',
            suggestions: ['Check JSON syntax']
        };
    }
};

// Auto-correction Functions
export const getCompatibleUITypes = (parameterType) => {
    const restrictions = FIELD_RESTRICTIONS.parameter_type[parameterType];
    return restrictions ? restrictions.allowed_types : [];
};

export const getSuggestedUIType = (parameterType, currentType) => {
    const compatible = getCompatibleUITypes(parameterType);
    
    // Define preferred UI types for each parameter type
    const preferences = {
        BOOLEAN: 'switch',
        STRING: 'text',
        INTEGER: 'number',
        FLOAT: 'number',
        JSON: 'textarea'
    };
    
    const preferredType = preferences[parameterType];
    
    // If preferred type is compatible, use it
    if (compatible.includes(preferredType)) {
        return preferredType;
    }
    
    // Fallback to first compatible type
    return compatible[0] || 'text';
};

export const getSuggestedOptionType = (uiType) => {
    const restrictions = FIELD_RESTRICTIONS.type[uiType];
    if (restrictions && restrictions.requires_options) {
        return 'STATIC'; // Default to STATIC for UI types that need options
    }
    return null; // No option type needed
};

// Comprehensive validation for entire form
export const validateFormCompatibility = (formValues) => {
    const errors = {};
    const warnings = {};

    // 1. Parameter Type ↔ UI Type compatibility
    const typeCompatibility = validateParameterTypeCompatibility(
        formValues.parameter_type, 
        formValues.type
    );
    if (!typeCompatibility.valid) {
        errors.type = typeCompatibility.message;
        warnings.type = `Suggested types: ${typeCompatibility.suggestions.join(', ')}`;
    }

    // 2. UI Type ↔ Options requirements
    const optionsValidation = validateOptionsRequirements(
        formValues.type,
        formValues.options,
        formValues.option_type,
        formValues.option_config
    );
    if (!optionsValidation.valid) {
        errors.options = optionsValidation.message;
        warnings.options = optionsValidation.suggestions.join(', ');
    }

    // 3. Option Type ↔ Config requirements
    const optionTypeValidation = validateOptionTypeRequirements(
        formValues.option_type,
        formValues.options,
        formValues.option_config
    );
    if (!optionTypeValidation.valid) {
        errors.option_type = optionTypeValidation.message;
        warnings.option_type = optionTypeValidation.suggestions.join(', ');
    }

    // 4. Validation Rules compatibility
    const validationRulesValidation = validateValidationRules(
        formValues.parameter_type,
        formValues.validation_rules
    );
    if (!validationRulesValidation.valid) {
        errors.validation_rules = validationRulesValidation.message;
        warnings.validation_rules = validationRulesValidation.suggestions.join(', ');
    }

    return {
        valid: Object.keys(errors).length === 0,
        errors,
        warnings
    };
};
